import { Outlet, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Moon, Sun } from 'lucide-react' // or your preferred icon library
import React from 'react'
import { NavLink } from '@/components/navigation/nav-link'

import { useAuth } from '@/components/providers/auth-provider'
import { useTheme } from '@/components/providers/theme-provider'
import {
  publicNavItems,
  authenticatedNavItems,
  hasAccess
} from '@/lib/navigation'
import { config } from '@/lib/config'





export function PublicLayout() {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') return 'dark'
    const stored = window.localStorage.getItem('theme')
    return stored === 'light' || stored === 'dark' ? stored : 'dark'
  })
  const { isAuthenticated, user } = useAuth()
  const navItems = isAuthenticated ? authenticatedNavItems : publicNavItems
  const userTier = user?.subscription_tier || 'free'


  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle('dark', theme === 'dark')
    window.localStorage.setItem('theme', theme)
  }, [theme])

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark')
  }

  return (
    <div className={`min-h-screen flex flex-col ${theme === 'dark' ? 'bg-[#05050a]' : 'bg-white'}`}>
      {/* Header */}
      <header className={`border-b ${theme === 'dark' ? 'border-transparent bg-[#030623]' : 'border-transparent bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="/images/ICON_WHITE.svg"
                alt="Digestr"
                className="h-8"
              />
            </Link>


            <nav className="hidden md:flex items-center space-x-1">
              {navItems
                .filter(item => isAuthenticated ? hasAccess(item, userTier) : true)
                .map((item) => (
                  <NavLink
                    key={item.key}
                    href={item.href}
                    icon={item.icon}
                    isActive={location.pathname === item.href}
                    locked={isAuthenticated && item.requiredTier && !hasAccess(item, userTier)}
                  >
                    {item.label}
                  </NavLink>
                ))}
            </nav>


            {/* Navigation + Theme Toggle + Auth */}
            <div className="flex items-center gap-6">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${theme === 'dark'
                  ? 'hover:bg-slate-800/50 text-slate-400'
                  : 'hover:bg-gray-100 text-gray-600'
                  }`}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* Auth buttons */}
              <Link
                to="/auth/login"
                className={`transition-colors ${theme === 'dark'
                  ? 'text-slate-400 hover:text-white'
                  : 'text-gray-700 hover:text-gray-900'
                  }`}
              >
                Sign In
              </Link>

            </div>
          </div>
        </div>
      </header>

      {/* Page content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className={`border-t py-12 ${theme === 'dark' ? 'border-slate-800 bg-[#030623]' : 'border-gray-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img
                src="/images/FULL_LOGO.svg"
                alt="Digestr"
                className="h-8 mb-4"
              />
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                Intelligence infrastructure for the information age
              </p>
            </div>

            <div>
              <h3 className={`font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Product
              </h3>
              <ul className={`space-y-2 text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                <li><Link to="/features" className={theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'}>Features</Link></li>
                <li><Link to="/pricing" className={theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'}>Pricing</Link></li>
                <li><a href="https://polari.dev" target="_blank" rel="noopener noreferrer" className={theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'}>API Docs</a></li>
              </ul>
            </div>

            <div>
              <h3 className={`font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Company
              </h3>
              <ul className={`space-y-2 text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                <li><Link to="/about" className={theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'}>About</Link></li>
                <li><a href="https://github.com/yourusername/digestr" target="_blank" rel="noopener noreferrer" className={theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'}>GitHub</a></li>
                <li><Link to="/contact" className={theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'}>Contact</Link></li>
              </ul>
            </div>

            <div>
              <h3 className={`font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Legal
              </h3>
              <ul className={`space-y-2 text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                <li><Link to="/privacy" className={theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'}>Privacy</Link></li>
                <li><Link to="/terms" className={theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'}>Terms</Link></li>
              </ul>
            </div>
          </div>

          <div className={`border-t pt-8 text-center text-sm ${theme === 'dark'
            ? 'border-slate-800 text-slate-500'
            : 'border-gray-200 text-gray-600'
            }`}>
            <p>&copy; 2025 Digestr. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}